import React from 'react';

import { useSelect } from 'downshift';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { brandFont } from 'components/layout/brand-font';
import UnstyledButton from 'components/unstyled-button';
import { primitive } from 'styles/constants/primitives';

import {
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
} from './mad-lib-select-accessories';
import { IFilter } from './types';

interface IIsOpen {
  isOpen: boolean;
}
const Container = styled.div`
  grid-area: select;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
`;

const SelectContainer = styled.div<IIsOpen>`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  padding: 0 ${primitive.$spacing2};
  box-shadow: ${p => (p.isOpen ? Styles.boxShadow : 'none')};
  border-radius: 8px 8px 0 0;

  ${Styles.desktop`
    flex-basis: 80%;
    padding: 0 ${primitive.$spacing4};
  `};
`;

const SelectContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: ${primitive.$spacing2} 0;
  overflow: hidden;

  ${Styles.desktop`
    padding: ${primitive.$spacing1} 0;
  `}
`;

const BlackBar = styled.div<IIsOpen>`
  width: 100%;
  height: 1px;
  background-color: ${p => p.theme.token('icon-default')};

  visibility: ${p => (p.isOpen ? 'hidden' : 'visible')};

  ${Styles.desktop`
    height: 3px;
  `}
`;

const SelectToggle = styled(UnstyledButton)`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const SelectLabel = styled.label`
  margin: 0 0 0 ${primitive.$spacing2};
  text-transform: ${Styles.textTransform.headlines};
  font: ${brandFont.copyOne};

  ${Styles.desktop`
    letter-spacing: 1px;
    margin-left: ${primitive.$spacing4};
  `}
`;

const textStyles = css`
  margin: 0;
  text-transform: ${Styles.textTransform.headlines};
  font: ${brandFont.headerThree};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  ${Styles.desktop`
    letter-spacing: 1px;
  `}
`;

const CurrentSelection = styled.h3`
  ${textStyles};
  color: ${p => p.theme.token('text-default')};
`;

const MenuContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MenuList = styled.ul<IIsOpen>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${Styles.zIndex.below};
  max-height: 12.5rem;
  width: 100%;

  list-style: none;
  padding: ${primitive.$spacing1} 0;
  margin: 0;
  overflow-y: auto;
  background-color: ${p => p.theme.token('background-pattern')};
  border-radius: 0 0 8px 8px;
  box-shadow: ${Styles.boxShadow};

  * + * {
    border-top: solid 1px ${p => p.theme.token('border-color-default')};
  }

  display: ${p => (p.isOpen ? 'inherit' : 'none')};

  ${Styles.desktop`
    padding: 0;
    max-height: 20rem;
  `}
`;

const SelectItem = styled.li<{ isHighlighted: boolean; isSelected: boolean }>`
  padding: ${primitive.$spacing1} ${primitive.$spacing2};
  color: ${p => (p.isSelected ? p.theme.token('text-reversed') : p.theme.token('text-default'))};

  & :hover {
    background-color: ${p =>
      !p.isSelected ? p.theme.token('background-focus') : p.theme.token('background-active')};
  }
  background-color: ${p =>
    p.isSelected
      ? p.theme.token('background-active')
      : p.isHighlighted
      ? p.theme.token('background-focus')
      : 'inherit'};

  ${Styles.desktop`
    padding: ${primitive.$spacing1} ${primitive.$spacing4};
  `};

  cursor: pointer;
`;

const SelectItemText = styled.h3`
  ${textStyles};
`;

const itemToString = (filter: IFilter | null) => filter?.description?.locale ?? '';

interface IMadLibSelect {
  prompt?: string;
  selectedFilter: IFilter | null;
  filters: IFilter[];
  onFilterChange: (filter?: IFilter | null) => void;
}

export const MadLibSelect: React.FC<IMadLibSelect> = ({
  onFilterChange,
  selectedFilter,
  filters,
  prompt,
}) => {
  const { formatMessage } = useIntl();
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    reset,
  } = useSelect({
    items: filters,
    itemToString,
    selectedItem: selectedFilter,
    onSelectedItemChange: changes => onFilterChange(changes.selectedItem),
  });

  const currentSelection = selectedItem
    ? selectedItem.description?.locale
    : formatMessage({ id: 'selectAFilter' });

  return (
    <Container>
      <SelectLabel {...getLabelProps()} aria-hidden>
        {prompt || formatMessage({ id: 'imCraving' })}
      </SelectLabel>
      <SelectContainer isOpen={isOpen}>
        <SelectContent>
          <SelectToggle {...getToggleButtonProps()}>
            <CurrentSelection>{currentSelection}</CurrentSelection>
            <DropdownIndicator isOpen={isOpen} />
          </SelectToggle>
          <IndicatorSeparator />
          <ClearIndicator onClick={reset} />
        </SelectContent>
        <BlackBar isOpen={isOpen} />
      </SelectContainer>
      <MenuContainer>
        <MenuList {...getMenuProps()} isOpen={isOpen}>
          {isOpen &&
            filters.map((filter, index) => (
              <SelectItem
                key={filter._id}
                isSelected={selectedItem?._id === filter._id}
                isHighlighted={highlightedIndex === index}
                {...getItemProps({
                  item: filter,
                  index,
                })}
              >
                <SelectItemText>{filter.description?.locale}</SelectItemText>
              </SelectItem>
            ))}
        </MenuList>
      </MenuContainer>
    </Container>
  );
};
export default MadLibSelect;
